import { useTranslation } from 'react-i18next';
import { useToast } from '@ntconsult/aluma-react';
import { triggerBrowserDownload } from '@common/utils/download';
import { shareService } from '../services/shareService';
import { parseShareError } from '../utils/shareError';

export type ShareDownloadTarget =
  | string
  | {
      /** Download de um arquivo específico dentro de uma pasta compartilhada. */
      fileId?: string;
      /**
       * Download de uma subpasta (descendente da pasta compartilhada) como ZIP.
       * Ignorado se `fileId` estiver presente.
       */
      folderId?: string;
      /**
       * Quando o share inteiro é uma pasta e o caller quer baixar a raiz como
       * ZIP. Combine com `folderId` ausente.
       */
      isFolder?: boolean;
      /** Nome sugerido ao salvar o ZIP (usado apenas no fallback via blob). */
      fallbackName?: string;
    };

/**
 * Helper para baixar conteúdo de um share público.
 *
 * Sem args / com string: baixa um arquivo (raiz do share ou específico).
 * Com `{ isFolder: true }` ou `{ folderId }`: baixa pasta como ZIP streamed.
 *
 * Quando há senha, força fluxo via blob (header `x-share-password`).
 * Sem senha, usa `<a download>` direto — streaming ao disco sem bufferizar.
 */
export function useShareDownload(
  token: string | undefined,
  password: string | undefined,
) {
  const { t } = useTranslation('shares');
  const { toast } = useToast();

  return async (target?: ShareDownloadTarget) => {
    if (!token) return;
    const normalized: Exclude<ShareDownloadTarget, string> =
      typeof target === 'string' ? { fileId: target } : (target ?? {});

    const { fileId, folderId, isFolder, fallbackName } = normalized;
    const wantsFolder = isFolder || !!folderId;

    try {
      if (wantsFolder) {
        const name = fallbackName ?? 'pasta.zip';
        if (password) {
          await shareService.downloadFolderShareViaBlob(
            token,
            folderId,
            password,
            name,
          );
        } else {
          triggerBrowserDownload(
            shareService.getFolderShareDownloadUrl(token, folderId),
          );
        }
        return;
      }

      const data = fileId
        ? await shareService.downloadFileInShare(token, fileId, password)
        : await shareService.downloadShare(token, password);
      triggerBrowserDownload(data.downloadUrl, data.fileName);
    } catch (err) {
      const parsed = parseShareError(err);
      toast.danger({ title: parsed.message || t('public.downloadError') });
    }
  };
}
